/**
 * Rashi reference data — names in English/Nepali/Hindi/Sanskrit plus the
 * naamakshar (naming syllables) traditionally used to pick a child's name and
 * to identify one's Moon sign when the birth time is unknown.
 *
 * Single source of truth: the previous copy lived inline in RasiPhalSection and
 * had Virgo's and Libra's syllable sets swapped, Taurus using वृ instead of वू,
 * and no Hindi at all. Import from here rather than re-declaring.
 */

export interface RashiInfo {
  id: number;
  /** URL segment for /rashifal/<slug> — romanised Sanskrit, the spelling people search. */
  slug: string;
  english: string;
  nepali: string;
  hindi: string;
  sanskrit: string;
  /** Common alternative romanisations, used to resolve incoming URLs. */
  aliases: string[];
  element: string;
  rulingPlanet: string;
  dateRange: string;
  syllablesNepali: string[];
  syllablesEnglish: string[];
}

export const RASHI_DATA: RashiInfo[] = [
  {
    id: 1,
    slug: 'mesh',
    english: 'Aries',
    nepali: 'मेष',
    hindi: 'मेष',
    sanskrit: 'मेष',
    aliases: ['aries', 'mesha', 'mesh-rashi'],
    element: 'Fire',
    rulingPlanet: 'Mars',
    dateRange: 'Mar 21 – Apr 19',
    syllablesNepali: ['चू', 'चे', 'चो', 'ला', 'ली', 'लू', 'ले', 'लो', 'अ'],
    syllablesEnglish: ['Chu', 'Che', 'Cho', 'La', 'Li', 'Lu', 'Le', 'Lo', 'A'],
  },
  {
    id: 2,
    slug: 'vrishabha',
    english: 'Taurus',
    nepali: 'वृष',
    hindi: 'वृषभ',
    sanskrit: 'वृषभ',
    aliases: ['taurus', 'vrish', 'vrishabh', 'brish'],
    element: 'Earth',
    rulingPlanet: 'Venus',
    dateRange: 'Apr 20 – May 20',
    syllablesNepali: ['ई', 'ऊ', 'ए', 'ओ', 'वा', 'वी', 'वू', 'वे', 'वो'],
    syllablesEnglish: ['Ee', 'U', 'E', 'O', 'Va', 'Vi', 'Vu', 'Ve', 'Vo'],
  },
  {
    id: 3,
    slug: 'mithun',
    english: 'Gemini',
    nepali: 'मिथुन',
    hindi: 'मिथुन',
    sanskrit: 'मिथुन',
    aliases: ['gemini', 'mithuna'],
    element: 'Air',
    rulingPlanet: 'Mercury',
    dateRange: 'May 21 – Jun 20',
    syllablesNepali: ['का', 'की', 'कू', 'घ', 'ङ', 'छ', 'के', 'को', 'ह'],
    syllablesEnglish: ['Ka', 'Ki', 'Ku', 'Gha', 'Nga', 'Chha', 'Ke', 'Ko', 'Ha'],
  },
  {
    id: 4,
    slug: 'karkat',
    english: 'Cancer',
    nepali: 'कर्कट',
    hindi: 'कर्क',
    sanskrit: 'कर्क',
    aliases: ['cancer', 'kark', 'karka'],
    element: 'Water',
    rulingPlanet: 'Moon',
    dateRange: 'Jun 21 – Jul 22',
    syllablesNepali: ['ही', 'हू', 'हे', 'हो', 'डा', 'डी', 'डू', 'डे', 'डो'],
    syllablesEnglish: ['Hi', 'Hu', 'He', 'Ho', 'Da', 'Di', 'Du', 'De', 'Do'],
  },
  {
    id: 5,
    slug: 'singha',
    english: 'Leo',
    nepali: 'सिंह',
    hindi: 'सिंह',
    sanskrit: 'सिंह',
    aliases: ['leo', 'simha', 'sinha'],
    element: 'Fire',
    rulingPlanet: 'Sun',
    dateRange: 'Jul 23 – Aug 22',
    syllablesNepali: ['मा', 'मी', 'मू', 'मे', 'मो', 'टा', 'टी', 'टू', 'टे'],
    syllablesEnglish: ['Ma', 'Mi', 'Mu', 'Me', 'Mo', 'Ta', 'Ti', 'Tu', 'Te'],
  },
  {
    id: 6,
    slug: 'kanya',
    english: 'Virgo',
    nepali: 'कन्या',
    hindi: 'कन्या',
    sanskrit: 'कन्या',
    aliases: ['virgo'],
    element: 'Earth',
    rulingPlanet: 'Mercury',
    dateRange: 'Aug 23 – Sep 22',
    syllablesNepali: ['टो', 'पा', 'पी', 'पू', 'ष', 'ण', 'ठ', 'पे', 'पो'],
    syllablesEnglish: ['To', 'Pa', 'Pi', 'Pu', 'Sha', 'Na', 'Tha', 'Pe', 'Po'],
  },
  {
    id: 7,
    slug: 'tula',
    english: 'Libra',
    nepali: 'तुला',
    hindi: 'तुला',
    sanskrit: 'तुला',
    aliases: ['libra'],
    element: 'Air',
    rulingPlanet: 'Venus',
    dateRange: 'Sep 23 – Oct 22',
    syllablesNepali: ['रा', 'री', 'रू', 'रे', 'रो', 'ता', 'ती', 'तू', 'ते'],
    syllablesEnglish: ['Ra', 'Ri', 'Ru', 'Re', 'Ro', 'Ta', 'Ti', 'Tu', 'Te'],
  },
  {
    id: 8,
    slug: 'vrishchik',
    english: 'Scorpio',
    nepali: 'वृश्चिक',
    hindi: 'वृश्चिक',
    sanskrit: 'वृश्चिक',
    aliases: ['scorpio', 'vrischika', 'brischik'],
    element: 'Water',
    rulingPlanet: 'Mars',
    dateRange: 'Oct 23 – Nov 21',
    syllablesNepali: ['तो', 'ना', 'नी', 'नू', 'ने', 'नो', 'या', 'यी', 'यू'],
    syllablesEnglish: ['To', 'Na', 'Ni', 'Nu', 'Ne', 'No', 'Ya', 'Yi', 'Yu'],
  },
  {
    id: 9,
    slug: 'dhanu',
    english: 'Sagittarius',
    nepali: 'धनु',
    hindi: 'धनु',
    sanskrit: 'धनु',
    aliases: ['sagittarius', 'dhanus'],
    element: 'Fire',
    rulingPlanet: 'Jupiter',
    dateRange: 'Nov 22 – Dec 21',
    syllablesNepali: ['ये', 'यो', 'भा', 'भी', 'भू', 'धा', 'फा', 'ढा', 'भे'],
    syllablesEnglish: ['Ye', 'Yo', 'Bha', 'Bhi', 'Bhu', 'Dha', 'Pha', 'Dha', 'Bhe'],
  },
  {
    id: 10,
    slug: 'makar',
    english: 'Capricorn',
    nepali: 'मकर',
    hindi: 'मकर',
    sanskrit: 'मकर',
    aliases: ['capricorn', 'makara'],
    element: 'Earth',
    rulingPlanet: 'Saturn',
    dateRange: 'Dec 22 – Jan 19',
    syllablesNepali: ['भो', 'जा', 'जी', 'खी', 'खू', 'खे', 'खो', 'गा', 'गी'],
    syllablesEnglish: ['Bho', 'Ja', 'Ji', 'Khi', 'Khu', 'Khe', 'Kho', 'Ga', 'Gi'],
  },
  {
    id: 11,
    slug: 'kumbha',
    english: 'Aquarius',
    nepali: 'कुम्भ',
    hindi: 'कुंभ',
    sanskrit: 'कुम्भ',
    aliases: ['aquarius', 'kumbh'],
    element: 'Air',
    rulingPlanet: 'Saturn',
    dateRange: 'Jan 20 – Feb 18',
    syllablesNepali: ['गू', 'गे', 'गो', 'सा', 'सी', 'सू', 'से', 'सो', 'दा'],
    syllablesEnglish: ['Gu', 'Ge', 'Go', 'Sa', 'Si', 'Su', 'Se', 'So', 'Da'],
  },
  {
    id: 12,
    slug: 'meen',
    english: 'Pisces',
    nepali: 'मीन',
    hindi: 'मीन',
    sanskrit: 'मीन',
    aliases: ['pisces', 'meena'],
    element: 'Water',
    rulingPlanet: 'Jupiter',
    dateRange: 'Feb 19 – Mar 20',
    syllablesNepali: ['दी', 'दू', 'थ', 'झ', 'ञ', 'दे', 'दो', 'चा', 'ची'],
    syllablesEnglish: ['Di', 'Du', 'Tha', 'Jha', 'Nya', 'De', 'Do', 'Cha', 'Chi'],
  },
];

export const rashiById = (id: number): RashiInfo | undefined =>
  RASHI_DATA.find((r) => r.id === id);

/** Comma-joined naamakshar for display. Devanagari for Nepali/Hindi, roman for English. */
export const rashiSyllables = (id: number, language: 'en' | 'ne' | 'hi'): string => {
  const r = rashiById(id);
  if (!r) return '';
  return (language === 'en' ? r.syllablesEnglish : r.syllablesNepali).join(', ');
};

/** Sign name in the requested language. */
export const rashiName = (id: number, language: 'en' | 'ne' | 'hi'): string => {
  const r = rashiById(id);
  if (!r) return '';
  return language === 'en' ? r.english : language === 'hi' ? r.hindi : r.nepali;
};

/** Resolve a URL segment to a rashi, accepting slug, English name, or a known alias. */
export const rashiBySlug = (slug: string): RashiInfo | undefined => {
  const key = decodeURIComponent(String(slug || '')).toLowerCase().trim();
  return RASHI_DATA.find(
    (r) => r.slug === key || r.english.toLowerCase() === key || r.aliases.includes(key),
  );
};

